
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import HeaderTitle from './HeaderTitle.vue';
import ActionButton from './ActionButton.vue';
import StoresSwitcher from './StoresSwitcher.vue';
import UpgradeModal from './UpgradeModal.vue';
import PinButton from './PinButton.vue';
import PinnedSection from './PinnedSection.vue';
import PinnedReportsModule from '@/store/modules/pinned-reports';
import { StoreModel } from '@/models/store';
import { EventBus } from '@/main';
import ForecastSetting from '@/models/forecast_setting';
import { FEATURES } from '@/config/featureConfig';
import {
  selectMenuConfig,
  versionSwitcherEntry,
  resolveReplenishmentEntitlement,
  atlasOptInMenuSection,
} from '@/config/menu/selectMenuConfig';
import { getActiveRouteMap, getAtlasRedirectTarget } from '@/config/atlasFeatures';
import { getModule } from 'vuex-module-decorators';
import MenuVersionModule from '@/store/modules/menu-version';
import PinnedItemsModule from '@/store/modules/pinned-items';
import store from '@/store';
import { EventHandler } from '@/modules/events';
import AtlasOptInDialog from '@/components/AtlasOptInDialog.vue';

@Component({
components: {
  HeaderTitle,
  ActionButton,
  StoresSwitcher,
  UpgradeModal,
  PinButton,
  PinnedSection,
  AtlasOptInDialog,
},
})
export default class VerticalMenuBar extends Vue {
public showAtlasOptIn: boolean = false;

private static SWITCHER_ICONS: { [switchToVersion: number]: string } = {
  2: 'mdi-backup-restore',
  3: 'mdi-star-outline',
};

private atlasRouteMap: Record<string, string> = getActiveRouteMap();

// Lazy initialization of EventHandler
private get eventHandler(): EventHandler {
  if (!this._eventHandler) {
    this._eventHandler = new EventHandler({ store: this.store });
  }
  return this._eventHandler;
}

// Entitlement input to the v3 shape decision (AS-10794). Reads
// store.rules through hasPermission, so this getter — and therefore
// getMenuItems — re-evaluates when getRules() lands and the merchant's real
// entitlements arrive; it is not a one-shot read at mount.
private get hasReplenishmentEntitlement(): boolean {
  return resolveReplenishmentEntitlement(
    Vue.prototype.$permissionsInitializing === true,
    this.store?.rules?.items?.length ?? 0,
    this.hasPermission(FEATURES.BUYING_TABLE),
  );
}

public get getMenuItems() {
  let menuData = selectMenuConfig(
    this.menuVersionStore.menuVersion,
    this.isFreeUser(),
    this.store.currentPackage?.name,
    this.hasReplenishmentEntitlement,
  );
  menuData = this.addVersionSwitcher(menuData);
  menuData = this.addAtlasOptIn(menuData);
  return this.rewriteLinksForAtlas(menuData);
}

// AS-11272 M6: reactive on `store.frontendAppOptInAvailable`, which the bot
// computes per caller — the entry appears on the next /stores/detail read.
private addAtlasOptIn(menuData: any[]) {
  const section = atlasOptInMenuSection(this.store?.frontendAppOptInAvailable === true);
  return section ? [...menuData, section] : menuData;
}

get canAccessOperationalMode(): boolean {
  const validateValues = [
    {
      code: 'operational_mode',
      value: 'unlocked',
    },
  ];
  const validateResult = this.store.validateRule(validateValues);
  return validateResult.isValidate;
}

public get isTestingStore() {
  const testingStores = [
    'dariustest2.myshopify.com',
    'the-best-mega-store.myshopify.com',
    '2dba1f-4.myshopify.com',
  ];
  return testingStores.includes(this.store.shopName);
}

public get isAssistyAdmin() {
  if (localStorage.getItem('loggedInFromHades') !== 'true') {
    return false;
  }
  return true;
}
@Prop({ default: () => new StoreModel() })
public store!: StoreModel;

public get lockMenuWhenDataNotReady(): boolean {
  return process.env.VUE_APP_LOCK_MENU_WHEN_DATA_NOT_READY === 'true';
}

public get isEtlStatusLoaded(): boolean {
  return this.store.etlStatuses && this.store.etlStatuses.items.length > 0;
}

public get showMenuLock(): boolean {
  if (this.$route.query.lock_menu === 'true') return true;
  return this.lockMenuWhenDataNotReady && this.isEtlStatusLoaded && !this.store.isAthenaReady;
}

public async onViewProgressClick() {
  this.eventHandler.track('View Progress Clicked', {
    source: 'locked_menu',
    store: this.store.shopName,
  });
  try {
    await this.$router.push('/data-importing');
  } catch (e) {
    // suppress navigation duplicated errors
  }
}
public mini: boolean = true;
@Prop({default: null})
public app!: any;
public search: string = '';
public menuPurpose: string = '';
public canExpandOnHover: boolean = true;
private forecastSetting = new ForecastSetting();
private currentMethodology: string = '';
private menuVersionStore = getModule(MenuVersionModule, store);
private pinnedItemsStore = PinnedItemsModule;
private pinnedReportsStore = PinnedReportsModule;
private _eventHandler: EventHandler | null = null;
private pageLoadTime: number = Date.now();
private upgradeModalShownTime: number = 0;
// Ensures trackMenuVersionLoaded fires exactly once per session, and only
// after a resolution backed by real store data (see mounted()/onStoreChange()).
private menuVersionLoadTracked = false;

// Enhanced state management for menu expansion
private expandedState = {
  sections: {} as { [key: string]: boolean },
  groups: {} as { [key: string]: boolean },
};

// Track current menu location (using titles only)
private currentMenuPath: {
  section?: string;
  group?: string;
  item?: string;
} = {};

// Track manually collapsed items to respect user preference
private manuallyCollapsed = new Set < string > ();

// Keep for backward compatibility temporarily
private expandedSections: { [key: string]: boolean } = {};

// Add data property
private showUpgradeModal: boolean = false;
private selectedLockedFeature: any = null;

public isFreeUser(): boolean {
  return this.store.currentPackage.name === 'Free';
}

public pinMenu() {
  this.canExpandOnHover = !this.canExpandOnHover;
  const isPinned = !this.canExpandOnHover;
  EventBus.$emit('vertical-menu-pinned', isPinned); // Emit this first
  if (isPinned) { // Menu is being pinned (mini = false, canExpandOnHover = false)
    this.mini = false;
    EventBus.$emit('vertical-menu-expanded');
  } else { // Menu is being unpinned (mini = true, canExpandOnHover = true)
    this.mini = true;
  }
  // EventBus.$emit('pin-menu', this.canExpandOnHover); // Old event can likely be removed if nothing else uses it
}

public searchReport() {
  this.$router.push('/report-library?search=' + this.search);
  this.search = '';
}

public doLogout() {
  this.$emit('logout');
}

public handleMenuClick(link: any, section?: string, group?: string, item?: string, menuItem?: any) {
  // Version switcher entries (see addVersionSwitcher) are unlocked leaf items,
  // so they reach this handler (not handleItemClick) via the template's normal
  // unlocked-item click binding below.
  if (menuItem?.switchToVersion) {
    this.menuVersionStore.chooseMenuVersion(menuItem.switchToVersion);
    this.eventHandler.track('Menu Version Switched', { to_version: menuItem.switchToVersion });
    return;
  }
  if (menuItem?.atlasOptIn) {
    this.showAtlasOptIn = true;
    return;
  }

  // Track the menu navigation
  if (section) {
    this.trackMenuNavigation(section, group, item, link);
  }

  // Existing logout logic
  if (link === '/logout') {
    this.doLogout();
  }
}
// Click handler for single-entry sections rendered as direct links (Today,
// Settings) — see template comment (user decision 2026-07-03). Navigation is
// handled by the v-list-item's `to` prop; this tracks the click AND, like
// handleMenuClick, persists currentMenuPath + the route mapping so
// restoration/highlighting works for these sections too (previously only
// analytics were tracked here, so Today/Settings never updated
// currentMenuPath — see trackMenuNavigation/isCurrentItem).
public handleSingleSectionClick(section: any): void {
  const timeSinceLoad = Date.now() - this.pageLoadTime;
  this.eventHandler.trackMenuItemClicked(this.menuVersionStore.menuVersion, section.title, timeSinceLoad);
  this.trackMenuNavigation(section.title, undefined, section.title, section.link);
}

public isPaidUser() {
  const isPaid: boolean = this.store.currentPackage.name !== 'Free';
  return isPaid;
}

public isCustomIcon(icon: string) {
  return icon && !icon.includes('mdi-');
}

public async created() {
  EventBus.$on('report-setup-open', async (isOpen: any) => {

  });

  await this.loadForecastMethodology();

  // Load pinned items for current store
  if (this.store && this.store.id) {
    await this.pinnedItemsStore.loadPinnedItems(this.store.id);
    await this.pinnedReportsStore.loadPinnedReports();
  }

  // Load saved state from localStorage
  this.loadExpandedState();

  // If no saved state exists, expand the first section as default
  if (Object.keys(this.expandedState.sections).length === 0) {
    const firstSection = this.getMenuItems[0];
    if (firstSection) {
      this.$set(this.expandedState.sections, firstSection.section || 'replenishment', true);
      this.$set(this.expandedSections, firstSection.section || 'replenishment', true);
    }
  }

  // Restore expanded groups from localStorage
  Object.keys(this.expandedState.groups).forEach((groupKey) => {
    if (this.expandedState.groups[groupKey]) {
      // Force Vue to recognize the change
      this.$set(this.expandedState.groups, groupKey, true);
    }
  });

  // Also restore the old expandedSections for compatibility
  Object.keys(this.expandedState.sections).forEach((sectionKey) => {
    if (this.expandedState.sections[sectionKey]) {
      this.$set(this.expandedSections, sectionKey, true);
    }
  });

  EventBus.$on('report-drawer-opened', this.handleReportDrawerOpen);
  EventBus.$on('report-drawer-closed', this.handleReportDrawerClosed);
}

public loadMenuStage() {
    // Assuming 'isPinMenu' is the intended key. If 'appMenuPinned' is correct from App.vue, this should be updated.
    const isInitiallyPinned = localStorage.getItem('isPinMenu') === 'true';

    if (isInitiallyPinned) {
      this.mini = false;
      this.canExpandOnHover = false; // Ensure this is correctly set for the pinned state
      EventBus.$emit('vertical-menu-pinned', true);
      EventBus.$emit('vertical-menu-expanded'); // Emit new event
    } else {
      this.mini = true;
      this.canExpandOnHover = true; // Ensure this is correctly set for the unpinned state
      EventBus.$emit('vertical-menu-pinned', false);
    }
  }

@Watch('mini', { immediate: true, deep: true })
public onExpandMenu(newVal: any) {
  if (!this.mini) {
    // When menu expands from mini mode, restore the saved expanded state
    this.loadExpandedState();

    const reportPageRouteNames: any = ['scheduleEditReport', 'templateReport', 'templateReportPage', 'customReport', 'detailReportPage', 'previewScheduledReport', 'viewCardReport', 'templateReportByType'];
    const drawerOpen = localStorage.getItem('drawerOpen') === 'true';
    // check if this is the TemplateReportPage component
    if ((!reportPageRouteNames.includes(this.$route.name)) || (reportPageRouteNames.includes(this.$route.name) && !drawerOpen)) {
      this.pinMenu();
    }
  }
}

// Method to check permission using the service
public hasPermission(featureCode: string): boolean {
  // Ensure $permissionService exists and is initialized
  if (Vue.prototype.$permissionsInitializing === true) {
    return true; // Deny access while initializing to avoid flickering
  }
  if (this.$permissionService) {
    return this.$permissionService.hasPermission(featureCode);
  }
  console.warn('Permission service not available in VerticalMenuBar for check:', featureCode);
  return false; // Default to no permission if service is missing
}

// Simple lock check - reuse existing hasPermission method
public isItemLocked(item: any): boolean {
  return item.permissionRequired && !this.hasPermission(item.permissionCode);
}

// Get required package for display
public getRequiredPackage(item: any): string {
  // First check if item has lockInfo with requiredPackage
  if (item.lockInfo?.requiredPackage) {
    return item.lockInfo.requiredPackage;
  }

  // Fallback logic based on permission code
  if (item.permissionCode?.includes('replenishment')) {
    return 'Replenishment';
  }
  if (item.permissionCode?.includes('analytics')) {
    return 'Analytics';
  }
  return 'Professional';
}

// Helper method to check if an item is pinned
public isPinned(section: string, itemTitle: string, groupTitle?: string): boolean {
  const itemId = this.generateItemId(section, itemTitle, groupTitle);
  return this.pinnedItemsStore.isPinned(itemId);
}

// Handle pin/unpin toggle
public async handlePinToggle(itemData: any): Promise<void> {
  const isPinned = await this.pinnedItemsStore.togglePinItem({
    item: itemData,
    storeId: this.store.id,
  });

  // If item was pinned, expand the Pinned section
  if (isPinned) {
    // Wait for the DOM to update after the item is added
    await this.$nextTick();

    // Now set the expanded state
    const pinnedSection = this.$refs.pinnedSection as any;
    if (pinnedSection) {
      pinnedSection.expanded = true;
    }
  }

  // Optional: Show feedback (could add a snackbar/toast here)
}

// Handle click on pinned item
public handlePinnedItemClick(item: any): void {
  // Track the click if needed
  // Navigation is handled by the router-link in PinnedSection component
}

// Handle item unpinned from pinned section
public handleItemUnpinned(item: any): void {
  // Could show a toast/snackbar here
}

// Simple click handler for locked items
public handleItemClick(item: any): void {
  const timeSinceLoad = Date.now() - this.pageLoadTime;

  if (item.switchToVersion) {
    this.menuVersionStore.chooseMenuVersion(item.switchToVersion);
    this.eventHandler.track('Menu Version Switched', { to_version: item.switchToVersion });
    return;
  }
  if (item.atlasOptIn) {
    this.showAtlasOptIn = true;
    return;
  }

  if (this.isItemLocked(item)) {
    // Track locked feature click
    const requiredPackage = item.lockInfo?.requiredPackage || this.getRequiredPackage(item);
    this.eventHandler.trackLockedFeatureClicked(item.title, requiredPackage);

    // Show upgrade modal
    this.selectedLockedFeature = {
      title: item.title,
      requiredPackage,
      description: item.lockInfo?.tooltipText || '',
      benefits: item.lockInfo?.benefits || [],
    };
    this.upgradeModalShownTime = Date.now();
    this.eventHandler.trackUpgradeModalShown(item.title, 'locked_feature');
    this.showUpgradeModal = true;
  } else {
    // Track menu item click
    this.eventHandler.trackMenuItemClicked(this.menuVersionStore.menuVersion, item.title, timeSinceLoad);

    // Normal navigation
    if (item.link) {
      this.$router.push(item.link);
    }
  }
}

public mounted() {
  this.resolveMenuVersionFromStore();
  // Track menu version loaded, but only if the store already has real data.
  // On a fresh load the `store` prop is still the empty StoreModel default,
  // so resolution here can land on the wrong version; onStoreChange() fires
  // the event instead once real store data lands (see menuVersionLoadTracked).
  if (this.store?.id != null && !this.menuVersionLoadTracked) {
    this.menuVersionLoadTracked = true;
    this.eventHandler.trackMenuVersionLoaded(this.menuVersionStore.menuVersion);
  }

  // Cleanup old route mappings periodically
  this.cleanupOldRouteMappings();
}

public beforeDestroy() {
  EventBus.$off('report-drawer-opened', this.handleReportDrawerOpen);
  EventBus.$off('report-drawer-closed', this.handleReportDrawerClosed);
  // No need to EventBus.$off('vertical-menu-pinned') here as this component doesn't listen to it.
}

/**
 * Appends a version-switcher "section" (reuses the existing section/group/item
 * shape so it renders with no new markup/CSS). The gating decision itself
 * (whether/what entry to show) is the pure, tested versionSwitcherEntry();
 * this method only wraps that decision in the menu-data shape.
 */
private addVersionSwitcher(menuData: any[]) {
  const entry = versionSwitcherEntry(this.menuVersionStore.menuVersion, this.isFreeUser());
  if (!entry) {
    return menuData;
  }
  return [...menuData, {
    section: 'menu-switcher', title: 'Menu style', icon: 'mdi-swap-horizontal', type: 'group',
    items: [{
      title: entry.title,
      icon: VerticalMenuBar.SWITCHER_ICONS[entry.switchToVersion],
      type: 'single',
      switchToVersion: entry.switchToVersion,
    }],
  }];
}

/**
 * Recursively rewrites menu item links for Atlas Setup mode.
 * Walks all sections and nested items, replacing any link found in atlasRouteMap.
 * Falls back to getAtlasRedirectTarget for links the static map misses (e.g.
 * hardcoded /board/:id entries in menu config), so the sidebar entry stays
 * highlighted rather than only redirecting on click via the router guard.
 */
private rewriteLinksForAtlas(menuData: any[]): any[] {
  return menuData.map((section: any) => {
    const newSection = { ...section };
    if (newSection.link) {
      const target = this.atlasRouteMap[newSection.link]
        ?? getAtlasRedirectTarget(newSection.link);
      if (target) {
        newSection.link = target;
      }
    }
    if (newSection.items) {
      newSection.items = this.rewriteLinksForAtlas(newSection.items);
    }
    return newSection;
  });
}

/**
 * Records the open/closed state VListGroup has already committed to.
 *
 * Bound to @input (the model event), NOT @click. VListGroup flips its own
 * `isActive` inside a $nextTick queued AFTER it emits `click`, so a handler
 * that also flips `:value` becomes a second writer: the parent's mutation
 * re-renders first, the Toggleable watcher syncs `isActive` to the new value,
 * and Vuetify's callback then inverts it. The two cancel, every click renders
 * the state from before the click, and collapsing an open section appears to
 * need two clicks. Taking `open` from the event leaves Vuetify the single
 * owner of the flip and this method a recorder of it.
 */
private onSectionToggle(sectionId: string, open: boolean) {
  // Find the section to check if it's locked
  const menuItems = this.getMenuItems as any[];
  const section = menuItems.find((s: any) => s.section === sectionId);

  // If section is locked, trigger upgrade modal instead of expanding
  if (section && section.permissionRequired && !this.hasPermission(section.permissionCode)) {
    // Track locked feature click for sections
    const requiredPackage = this.getRequiredPackage(section);
    this.eventHandler.trackLockedFeatureClicked(section.title, requiredPackage);

    this.selectedLockedFeature = {
      ...section,
      currentPackage: this.store.currentPackage.name,
      requiredPackage,
    };

    // Track upgrade modal shown
    this.upgradeModalShownTime = Date.now();
    this.eventHandler.trackUpgradeModalShown(section.title, 'locked_feature');
    this.showUpgradeModal = true;

    // Vuetify has already opened the group. Drive the bound value true ->
    // false so Toggleable's watcher sees a real change and shuts it again;
    // assigning false directly would be a no-op and leave it stuck open.
    this.$set(this.expandedState.sections, sectionId, true);
    this.$nextTick(() => {
      this.$set(this.expandedState.sections, sectionId, false);
      this.$set(this.expandedSections, sectionId, false);
    });
    return;
  }

  // Track menu section click for non-locked sections
  const timeSinceLoad = Date.now() - this.pageLoadTime;
  this.eventHandler.trackMenuItemClicked(this.menuVersionStore.menuVersion, section?.title || sectionId, timeSinceLoad);

  // Track manual interaction
  if (!open) {
    this.manuallyCollapsed.add(sectionId);
  } else {
    this.manuallyCollapsed.delete(sectionId);
  }

  if (open) {
    // Accordion: collapse all other sections and their groups.
    Object.keys(this.expandedState.sections).forEach((key) => {
      this.$set(this.expandedState.sections, key, false);
    });
    Object.keys(this.expandedSections).forEach((key) => {
      this.$set(this.expandedSections, key, false);
    });
    Object.keys(this.expandedState.groups).forEach((key) => {
      this.$set(this.expandedState.groups, key, false);
    });
  }

  // Record the state Vuetify committed to
  this.$set(this.expandedState.sections, sectionId, open);
  this.$set(this.expandedSections, sectionId, open); // Keep old one for compatibility

  // If we're expanding the section, also expand all groups within it
  if (open && section) {
    // Use nextTick to ensure Vue has updated the DOM for the section expansion
    this.$nextTick(() => {
      // Expand all groups in this section
      section.items?.forEach((item: any) => {
        if (item.type === 'group' && !this.isItemLocked(item)) {
          const groupKey = `${sectionId}.${item.id || item.title}`;
          this.$set(this.expandedState.groups, groupKey, true);
          // Remove from manually collapsed since we're auto-expanding
          this.manuallyCollapsed.delete(groupKey);
        }
      });
      // Force update and save state
      this.$forceUpdate();
      this.saveExpandedState();
    });
  }

  // Save state
  this.saveExpandedState();
}

/**
 * Sub-group counterpart to onSectionToggle — same @input contract, for the
 * same reason: a nested v-list-group double-toggles just as readily if the
 * parent drives its :value from @click.
 */
private onSubSectionToggle(sectionId: string, groupId: string | undefined, open: boolean) {
  if (!groupId) {
    // Old behavior for compatibility
    Object.keys(this.expandedSections).forEach((key) => {
      if (key !== sectionId) {
        this.$set(this.expandedSections, key, false);
      }
    });
    return;
  }

  const groupKey = `${sectionId}.${groupId}`;

  // Track manual interaction
  if (!open) {
    this.manuallyCollapsed.add(groupKey);
  } else {
    this.manuallyCollapsed.delete(groupKey);
  }

  // Record the state Vuetify committed to
  this.$set(this.expandedState.groups, groupKey, open);

  // Ensure parent section is expanded
  if (open && !this.expandedState.sections[sectionId]) {
    this.$set(this.expandedState.sections, sectionId, true);
    this.$set(this.expandedSections, sectionId, true); // Compatibility
  }

  // Save state
  this.saveExpandedState();
}

private async loadForecastMethodology() {
  try {
    await this.forecastSetting.load();
    this.currentMethodology = this.forecastSetting.options?.methodology || 'simple_average';
  } catch (error) {
    this.currentMethodology = 'simple_average';
  }
}

@Watch('store.id')
private async onStoreChange(newStoreId: number, oldStoreId: number): Promise<void> {
  // When store changes, reload pinned items for the new store
  if (newStoreId && newStoreId !== oldStoreId) {
    await this.pinnedItemsStore.clearPinnedItems();
    await this.pinnedItemsStore.loadPinnedItems(newStoreId);
    await this.pinnedReportsStore.clearPinnedReports();
    await this.pinnedReportsStore.loadPinnedReports();
    // Re-resolve the menu version once real store data lands (store.id
    // changes from the empty StoreModel default to the fetched model).
    // Idempotent: an explicit menuVersionChoice always wins over defaults,
    // so re-running this on every store.id change is safe.
    this.resolveMenuVersionFromStore();

    // If mounted() couldn't fire the load-tracked event (store wasn't ready
    // yet), fire it now that resolution has real store data. Fires exactly
    // once per session, whichever path wins.
    if (!this.menuVersionLoadTracked) {
      this.menuVersionLoadTracked = true;
      this.eventHandler.trackMenuVersionLoaded(this.menuVersionStore.menuVersion);
    }
  }
}

@Watch('$route')
private onRouteChange(newRoute: any): void {
  // Re-check atlas feature preferences on navigation
  this.atlasRouteMap = getActiveRouteMap();
}

private openChat(): void {
  const openChatMessage = 'I would like to unlock the Replenishment Center feature';
  // @ts-ignore: Unreachable code error
  this.$crisp.do('chat:open');
  // @ts-ignore: Unreachable code error
  this.$crisp.do('message:send', ['text', openChatMessage]);
}

// Generate item ID for pinning (matches the format in Vuex module)
private generateItemId(section: string, itemTitle: string, groupTitle?: string): string {
  const sanitize = (str: string) => str
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

  const parts = [section];
  if (groupTitle) {
    parts.push(sanitize(groupTitle));
  }
  parts.push(sanitize(itemTitle));

  return parts.join('-');
}

private handleReportDrawerOpen(): void {
  this.mini = true;
  this.canExpandOnHover = true;
  EventBus.$emit('vertical-menu-pinned', false);
}

private handleReportDrawerClosed(): void {
  this.mini = false;
  this.canExpandOnHover = false;
  EventBus.$emit('vertical-menu-pinned', true);
  EventBus.$emit('vertical-menu-expanded'); // Emit new event
}

private resolveMenuVersionFromStore() {
  this.menuVersionStore.initializeMenuVersion({
    merchantId: this.store?.id ?? null,
    isReplenishmentPackage: !!this.store?.currentPackage?.name?.toLowerCase().includes('replenishment'),
  });
}

private openExternalLink(url: string): void {
    window.open(url, '_blank');
  }

  private handleLearnMore(packageName: string): void {
    // Track Learn More click
    const timeOnModal = Date.now() - this.upgradeModalShownTime;
    this.eventHandler.trackUpgradeModalLearnMoreClicked(this.selectedLockedFeature?.title || '', timeOnModal);

    // Navigate to subscription page
    this.$router.push('/subscription');
  }

  // Dynamic menu path discovery methods
  private findMenuPathByRoute(route: string): {
    section?: string;
    sectionId?: string;
    group?: string;
    groupId?: string;
    item?: string;
  } | null {
    const menuItems = this.getMenuItems;

    for (const section of menuItems) {
      // Check direct section items
      for (const item of section.items || []) {
        if (item.type === 'group') {
          // Check sub-items in groups
          for (const subItem of item.items || []) {
            if (subItem.link === route) {
              return {
                section: section.title,      // Title for display/storage
                sectionId: section.section,  // ID for expandedState keys
                group: item.title,           // Title for display/storage
                groupId: item.id || item.title, // ID for expandedState keys
                item: subItem.title,          // Title for display/storage
              };
            }
          }
        } else if (item.link === route) {
          // Direct item under section
          return {
            section: section.title,      // Title for display/storage
            sectionId: section.section,  // ID for expandedState keys
            item: item.title,             // Title for display/storage
          };
        }
      }
    }

    // Check saved route mappings as fallback
    return this.loadRouteMenuPath(route);
  }

  // Track menu clicks to build route mapping dynamically
  private trackMenuNavigation(section: string, group?: string, item?: string, link?: string): void {
    // Update current path with titles only (no link to save space)
    // When group is undefined and item has a value, it means it's a group-level item
    this.currentMenuPath = {
      section,
      group: group || undefined,
      item: item || undefined,
    };

    // Save this route-to-path mapping for future use
    if (link) {
      // Extract base path without query params for mapping
      const basePath = link.split('?')[0];
      this.saveRouteMenuPath(basePath, { section, group, item });
    }

    // Immediately save the expanded state with updated currentPath
    this.saveExpandedState();
  }

  // Load route-to-menu-path mapping
  private loadRouteMenuPath(route: string): any {
    try {
      const storageKey = `menu_routes_${this.store.id}`;
      const existing = localStorage.getItem(storageKey);
      if (existing) {
        const routeMap = JSON.parse(existing);
        return routeMap[route] || null;
      }
    } catch (error) {
      console.warn('Failed to load route mapping:', error);
    }
    return null;
  }

  // Save route-to-menu-path mapping
  private saveRouteMenuPath(route: string, path: any): void {
    try {
      const storageKey = `menu_routes_${this.store.id}`;
      const existing = localStorage.getItem(storageKey);
      const routeMap = existing ? JSON.parse(existing) : {};
      routeMap[route] = { ...path, timestamp: Date.now() };

      // Keep only last 100 routes to prevent unlimited growth
      const routes = Object.keys(routeMap);
      if (routes.length > 100) {
        delete routeMap[routes[0]];
      }

      localStorage.setItem(storageKey, JSON.stringify(routeMap));
    } catch (error) {
      console.warn('Failed to save route mapping:', error);
    }
  }

  private getMenuVersion(): number {
    let menuVersion = 1;
    const menuVersionStore = this.menuVersionStore?.menuVersion;
    if (!menuVersionStore) {
      const savedVersion = localStorage.getItem('menuVersion');
      menuVersion = savedVersion ? parseInt(savedVersion, 10) as 1 | 2 : 1;
    }
    return menuVersion;
  }
  // Get unique storage key per store and menu version
  private getStorageKey(): string {
    // Get menu version from localStorage if menuVersionStore is not properly initialized
    const storeId = this.store.id;
    const menuVersion = this.getMenuVersion();
    return `menu_state_${storeId}_v${menuVersion}`;
  }

  // Save expanded state
  private saveExpandedState(): void {
    try {
      // Get the actual menu version
      const menuVersion = this.getMenuVersion();

      const state = {
        sections: this.expandedState.sections,
        groups: this.expandedState.groups,
        manuallyCollapsed: Array.from(this.manuallyCollapsed),
        currentPath: this.currentMenuPath,
        timestamp: Date.now(),
        version: menuVersion,  // Use the actual menu version instead of hardcoding 1
      };
      localStorage.setItem(this.getStorageKey(), JSON.stringify(state));
    } catch (error) {
      console.warn('Failed to save menu state:', error);
    }
  }

  // Load expanded state
  private loadExpandedState(): void {
    try {
      const saved = localStorage.getItem(this.getStorageKey());
      if (saved) {
        const state = JSON.parse(saved);
        this.expandedState.sections = state.sections || {};

        // Handle both old format (just group name) and new format (section.group)
        const groups = state.groups || {};
        const convertedGroups: { [key: string]: boolean } = {};

        // Convert old format to new format if needed
        Object.keys(groups).forEach((key) => {
          if (key.includes('.')) {
            // Already in new format
            convertedGroups[key] = groups[key];
          } else {
            // Old format - need to find which section this group belongs to
            // Use currentPath to determine the section if available
            if (state.currentPath && state.currentPath.section && state.currentPath.group === key) {
              convertedGroups[`${state.currentPath.section}.${key}`] = groups[key];
            } else {
              // Try to find the group in the menu structure
              const menuItems = this.getMenuItems;
              for (const section of menuItems) {
                for (const item of section.items || []) {
                  if (item.type === 'group' && (item.id === key || item.title === key)) {
                    convertedGroups[`${section.section}.${key}`] = groups[key];
                    break;
                  }
                }
              }
            }
          }
        });

        this.expandedState.groups = convertedGroups;
        this.manuallyCollapsed = new Set(state.manuallyCollapsed || []);
        this.currentMenuPath = state.currentPath || {};

        // If we have a currentPath with a group and item, ensure that group is expanded
        if (this.currentMenuPath.section && this.currentMenuPath.group && this.currentMenuPath.item) {
          // Find the section in the menu
          const menuItems = this.getMenuItems;
          const section = menuItems.find((s) => s.title === this.currentMenuPath.section);
          if (section && section.section) {
            // Expand the section
            this.$set(this.expandedState.sections, section.section, true);
            this.$set(this.expandedSections, section.section, true);

            // Find and expand the group
            const group = section.items?.find((g: any) => g.title === this.currentMenuPath.group);
            if (group && group.type === 'group') {
              const groupKey = `${section.section}.${group.id || group.title}`;
              this.$set(this.expandedState.groups, groupKey, true);

              // Remove from manually collapsed if it was there
              this.manuallyCollapsed.delete(groupKey);
            }
          }
        }
      }
    } catch (error) {
      console.warn('Failed to load menu state:', error);
      this.expandedState = { sections: {}, groups: {} };
    }
  }

  // Check if item is the current selected item
  private isCurrentItem(itemTitle: string): boolean {
    if (!itemTitle || !this.currentMenuPath.item) { return false; }

    // Check if this item title matches the current item in currentMenuPath
    return this.currentMenuPath.item === itemTitle;
  }

  // Cleanup old route mappings
  private cleanupOldRouteMappings(): void {
    try {
      const storageKey = `menu_routes_${this.store.id}`;
      const existing = localStorage.getItem(storageKey);
      if (existing) {
        const routeMap = JSON.parse(existing);
        const oneWeekAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);

        // Remove entries older than a week
        Object.keys(routeMap).forEach((route) => {
          if (routeMap[route].timestamp && routeMap[route].timestamp < oneWeekAgo) {
            delete routeMap[route];
          }
        });

        localStorage.setItem(storageKey, JSON.stringify(routeMap));
      }
    } catch (error) {
      console.warn('Failed to cleanup route mappings:', error);
    }
  }
}
